import type { RotaPatternResponse } from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { normalizeErrorMessage, showToast } from 'common/utils'
import { useMutation, useQueryClient } from 'react-query'
import {
  ROTA_PATTERN_DELETE_QUERY_KEY,
  ROTA_PATTERN_QUERY_KEY,
  ROTA_PATTERN_URL
} from './queryConstants'

type UseRotaPatternDeleteProps = {
  id?: string
  onDeleteSuccess?: () => void
}

type UseRotaPatternDeleteReturn = {
  deletePattern: () => Promise<unknown>
  isLoadingDelete?: boolean
}

export const useRotaPatternDelete = ({
  id,
  onDeleteSuccess
}: UseRotaPatternDeleteProps): UseRotaPatternDeleteReturn => {
  const queryClient = useQueryClient()

  const handleError = (err: any) => {
    showToast({
      title: normalizeErrorMessage(err),
      type: 'error'
    })
  }

  // DELETE PATTERN
  const deleteFunction = async (): Promise<RotaPatternResponse> => {
    return postJSON({
      url: `${ROTA_PATTERN_URL}/${id}`,
      method: 'DELETE',
      isRota: true
    })
  }

  const { mutateAsync: deleteMutation, isLoading: isLoadingDelete } =
    useMutation<RotaPatternResponse, unknown, unknown, unknown>(
      [ROTA_PATTERN_DELETE_QUERY_KEY, id],
      deleteFunction,
      {
        onSuccess: () => {
          queryClient.invalidateQueries(ROTA_PATTERN_QUERY_KEY)
          onDeleteSuccess?.()
        },
        onError: (err) => handleError(err)
      }
    )

  const handleDelete = async () => {
    try {
      const result = await deleteMutation({})
      return result
    } catch (err) {
      return err
    }
  }

  return {
    deletePattern: handleDelete,
    isLoadingDelete
  }
}
